import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { useVerticalScrollMask } from "@redotech/redo-web/utils/use-vertical-scroll-mask";
import classNames from "classnames";
import type { ReactNode, WheelEvent } from "react";
import { memo, useCallback, useEffect, useState } from "react";
import * as styles from "./scrollable-step-layout.module.css";
import {
  getTitleContentRevealDelayMs,
  StepMotionGroup,
  StepMotionItem,
} from "./step-motion";
import { StepTitle } from "./step-title";

export interface ScrollableStepLayoutProps {
  animateItems?: boolean;
  children: ReactNode;
  stepNumber: number;
  subtitle: ReactNode;
  title: string;
}

export const ScrollableStepLayout = memo(function ScrollableStepLayout({
  animateItems,
  children,
  stepNumber,
  subtitle,
  title,
}: ScrollableStepLayoutProps) {
  const contentRevealDelayMs = getTitleContentRevealDelayMs(title);
  const { scrollRef, contentRef, handleScroll, edges, maskImage } =
    useVerticalScrollMask();
  const [contentRevealStarted, setContentRevealStarted] =
    useState(!animateItems);
  const maskClassName =
    maskImage == null
      ? undefined
      : edges.atStart
        ? styles.maskTop
        : edges.atEnd
          ? styles.maskBottom
          : styles.maskMiddle;
  const hasOverflow = edges.scrollable;
  const canScrollDown = contentRevealStarted && hasOverflow && !edges.atEnd;

  useEffect(() => {
    setContentRevealStarted(!animateItems);
    if (!animateItems) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setContentRevealStarted(true);
    }, contentRevealDelayMs);

    return () => window.clearTimeout(timeout);
  }, [animateItems, contentRevealDelayMs, stepNumber, title]);

  useEffect(() => {
    const animationFrame = requestAnimationFrame(handleScroll);
    return () => cancelAnimationFrame(animationFrame);
  }, [handleScroll, stepNumber, title]);

  const handleWheel = useCallback(
    (event: WheelEvent) => {
      const scrollElement = scrollRef.current;
      if (!scrollElement) {
        return;
      }

      const maxScrollTop =
        scrollElement.scrollHeight - scrollElement.clientHeight;
      if (maxScrollTop <= 0) {
        return;
      }

      event.preventDefault();
      scrollElement.scrollTop = Math.max(
        0,
        Math.min(maxScrollTop, scrollElement.scrollTop + event.deltaY),
      );
      handleScroll();
    },
    [handleScroll, scrollRef],
  );

  const handleScrollDownClick = useCallback(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) {
      return;
    }

    scrollElement.scrollBy({
      behavior: "smooth",
      top: Math.max(160, scrollElement.clientHeight * 0.75),
    });
  }, [scrollRef]);

  return (
    <Flex
      className={styles.root}
      dir="column"
      height="full"
      onWheel={handleWheel}
    >
      <Flex className={styles.header} dir="column" gap="lg" pb="xl" shrink={0}>
        <StepMotionGroup animateItems={animateItems}>
          <Flex dir="column" gap="lg">
            <StepMotionItem animateItems={animateItems}>
              <Text fontSize="sm" textColor="quaternary">
                Step {stepNumber}
              </Text>
            </StepMotionItem>
            <StepMotionItem animateItems={animateItems}>
              <StepTitle animate={animateItems} text={title} />
            </StepMotionItem>
            <StepMotionItem
              animateItems={animateItems}
              delayMs={contentRevealDelayMs}
            >
              <Text fontSize="lg" textColor="quaternary">
                {subtitle}
              </Text>
            </StepMotionItem>
          </Flex>
        </StepMotionGroup>
      </Flex>

      <div
        className={classNames(styles.viewport, maskClassName)}
        onScroll={handleScroll}
        ref={scrollRef}
      >
        <StepMotionGroup animateItems={animateItems}>
          <Flex dir="column" gap="md" ref={contentRef}>
            {children}
          </Flex>
        </StepMotionGroup>
      </div>

      {hasOverflow ? (
        <RedoButton
          aria-label="Scroll down"
          buttonClassName={classNames(
            styles.scrollDownButton,
            !canScrollDown && styles.scrollDownButtonHidden,
          )}
          hierarchy="secondary"
          IconLeading={ChevronDownIcon}
          onClick={handleScrollDownClick}
          size="sm"
          text=""
        />
      ) : null}
    </Flex>
  );
});
